/**
 * useInstallBanner — the "add to home screen" invitation's state, shared by
 * the two surfaces that offer an install.
 *
 * There are two installable apps on this origin, and which one a tap installs
 * is decided by whichever manifest is linked at that moment — not by this
 * hook. Inside a gym with `gymPwa` on, that is the gym's own manifest
 * (`/manifest/{slug}.json`, its name, its icon, `start_url: /gym/{slug}`); on
 * the gym selector it is the platform `/manifest.json`. So the same banner
 * installs the gym on a gym page and the whole of Sendly on the selector, and
 * both are wanted. The platform install was already a supported case — the PWA
 * back button distinguishes the two by install scope (`d1b4a7b`) — there was
 * just nothing inviting it.
 *
 * **One dismiss flag for both**, deliberately: `sendly-pwa-dismissed` is not
 * scoped to a gym. Somebody who waved the invitation away has said no to being
 * asked to install Sendly, not no to this one gym, and a per-surface flag would
 * mean asking the same person again on the next page.
 *
 * `eligible` is the caller's own gate (the `gymPwa` feature flag and a resolved
 * slug in a gym; simply `true` on the selector). The banner only ever turns
 * itself ON here — it goes off through `dismissInstallBanner` or a completed
 * install.
 *
 * Analytics (`pwa_install_banner`): `shown` once per browser session when the
 * banner turns on, `dismiss` from its close button, and the native installer's
 * outcome. Nothing is logged for `beforeinstallprompt` itself — the browser
 * fires it on every page load until the app is installed, so it counts loads,
 * not invitations (that was the old `pwa_install_prompt`).
 */
import { useState, useEffect, useCallback } from "react";
import { logEvent, EVT } from "../analytics";
import { localStore, sessionStore } from "../utils/safeStorage";

const DISMISSED_KEY = "sendly-pwa-dismissed";
const SHOWN_SESSION_KEY = "sendly-pwa-banner-shown";

function bannerParams(gymId, action) {
  return gymId ? { gym_id: gymId, action } : { action };
}

// One `shown` per browser session, whichever surface shows it first. The
// marker is set only once the event really went out: before the cookie banner
// is answered logEvent drops it, and the next mount after consent may still
// count the session. Without sessionStorage it logs per mount rather than not
// at all.
function logShownOncePerSession(gymId) {
  if (sessionStore.get(SHOWN_SESSION_KEY)) return;
  if (!logEvent(EVT.PWA_INSTALL_BANNER, bannerParams(gymId, "shown"))) return;
  sessionStore.set(SHOWN_SESSION_KEY, "1");
}

export function useInstallBanner({ isStandalone, eligible = true, gymId } = {}) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Capture beforeinstallprompt — the stashed event is the ONLY way to open
  // the native installer later, and it is offered once.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      logEvent(EVT.PWA_INSTALLED, gymId ? { gym_id: gymId } : {});
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, [gymId]);

  useEffect(() => {
    if (!eligible || isStandalone) return;
    // Unreadable storage reads as "not dismissed": ask, rather than stay silent forever.
    if (localStore.get(DISMISSED_KEY)) return;
    setShowInstallBanner(true);
    logShownOncePerSession(gymId);
  }, [eligible, isStandalone, gymId]);

  const handleInstallPwa = useCallback(async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      logEvent(EVT.PWA_INSTALL_BANNER, bannerParams(gymId, outcome === "accepted" ? "prompt_accepted" : "prompt_dismissed"));
      if (outcome === "accepted") setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  }, [installPrompt, gymId]);

  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    localStore.set(DISMISSED_KEY, "1");
    logEvent(EVT.PWA_INSTALL_BANNER, bannerParams(gymId, "dismiss"));
  }, [gymId]);

  return { installPrompt, showInstallBanner, handleInstallPwa, dismissInstallBanner };
}
